import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Button from '@material-ui/core/Button';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import useEntries, { assetUrl, toExcerpt } from '../Common/useEntries';
import Publication from '../Publications/Publication';
import Pager from '../Common/Pager';
import SocialShare from '../Common/SocialShare';
import {
    PageHero,
    Section,
    SectionHeading,
    RichText,
    LoadingState,
    EmptyState,
    FilterBar,
    FilterLabel,
} from '../Common/PageLayout';
import { tokens } from '../../theme';
import { directorateName, liveSubdomainForDirectorate } from '../../subdomains/portal';
import useSubdomainUrl from '../../subdomains/useSubdomainUrl';

const PER_PAGE = 12;
const ALL = 'All';

const useStyles = makeStyles((theme) => ({
    photo: {
        width: '100%',
        maxHeight: 360,
        objectFit: 'cover',
        display: 'block',
        borderRadius: tokens.radius,
        border: `1px solid ${tokens.border}`,
        marginBottom: theme.spacing(3),
    },
    aside: {
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radius,
        backgroundColor: tokens.surface,
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    asideTitle: {
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: tokens.muted,
        margin: `0 0 ${theme.spacing(1.5)}px`,
    },
    stat: {
        fontFamily: tokens.headingFont,
        fontSize: '2rem',
        fontWeight: 700,
        color: tokens.blue800,
        lineHeight: 1.1,
    },
    statLabel: {
        color: tokens.muted,
        fontSize: '0.875rem',
    },
    jumpLink: {
        display: 'inline-block',
        marginTop: theme.spacing(1.5),
        color: tokens.blue600,
        fontWeight: 600,
        fontSize: '0.9375rem',
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
}));

/**
 * Everything about one directorate: its profile from Strapi and every
 * publication whose `filesowner` is this directorate.
 *
 * `onSubdomain` is set when the page is the home page of a directorate's own
 * subdomain (e.g. budget.mof.gov.tl), where main-portal breadcrumbs don't apply.
 */
export default function DirectorateProfile({ slug, onSubdomain = false }) {
    const classes = useStyles();
    const siteUrl = useSubdomainUrl();
    const directorate = useEntries({ content_type: 'directorates', 'fields.slug': slug, limit: 1 });
    const publications = useEntries(
        {
            content_type: 'publications',
            'fields.filesowner.slug': slug,
            order: '-sys.createdAt',
            limit: 100,
        },
        { all: true }
    );

    const [term, setTerm] = useState('');
    const [category, setCategory] = useState(ALL);
    const [page, setPage] = useState(1);

    const categories = useMemo(() => {
        const names = new Set(
            publications.items.map((entry) => entry.fields?.publicationCategory).filter(Boolean)
        );
        return [ALL, ...Array.from(names).sort()];
    }, [publications.items]);

    const filtered = useMemo(() => {
        const needle = term.trim().toLowerCase();
        return publications.items.filter(
            (entry) =>
                (category === ALL || entry.fields?.publicationCategory === category) &&
                (!needle || (entry.fields?.title || '').toLowerCase().includes(needle))
        );
    }, [publications.items, term, category]);

    if (directorate.loading) {
        return <LoadingState label="Loading directorate" />;
    }

    const fields = directorate.items[0]?.fields;
    if (!fields) {
        return (
            <Section>
                <EmptyState
                    title="Directorate not found"
                    description="This directorate may have been renamed or removed. Browse the full list of directorates instead."
                    action={{ label: 'All directorates', to: '/directorates' }}
                />
            </Section>
        );
    }

    const name = directorateName(fields);
    const photo = assetUrl(fields.photoProdirectorate || fields.logodirectorate);
    const profile = fields.contentprofile || fields.contentdescription;
    const shareUrl = `https://www.mof.gov.tl/directoratedetails/${fields.slug}`;
    const liveSubdomain = liveSubdomainForDirectorate(directorate.items[0]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const visible = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    return (
        <>
            <Helmet>
                <title>{`${name} | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={toExcerpt(profile, 155)} />
            </Helmet>

            <PageHero
                eyebrow="Directorate"
                title={name}
                description={toExcerpt(profile, 220)}
                breadcrumbs={
                    onSubdomain
                        ? undefined
                        : [
                              { label: 'About the Ministry', to: '/profile' },
                              { label: 'Directorates', to: '/directorates' },
                              { label: name },
                          ]
                }
            />

            <Section>
                <Grid container spacing={5}>
                    <Grid item xs={12} md={8}>
                        <SectionHeading overline="Profile" title={`About the ${name}`} />
                        {photo && <img src={photo} alt={name} className={classes.photo} crossOrigin="anonymous" />}
                        {profile ? (
                            <RichText html={profile} />
                        ) : (
                            <p>No profile has been published for this directorate yet.</p>
                        )}
                    </Grid>

                    <Grid item xs={12} md={4}>
                        {!onSubdomain && liveSubdomain && (
                            <div className={classes.aside}>
                                <p className={classes.asideTitle}>Directorate website</p>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    href={siteUrl(liveSubdomain)}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    {`Visit ${liveSubdomain}.mof.gov.tl`}
                                </Button>
                            </div>
                        )}
                        <div className={classes.aside}>
                            <p className={classes.asideTitle}>Publications</p>
                            <div className={classes.stat}>
                                {publications.loading ? '…' : publications.items.length}
                            </div>
                            <div className={classes.statLabel}>documents and reports</div>
                            <a className={classes.jumpLink} href="#directorate-publications">
                                Browse publications
                            </a>
                        </div>
                        <div className={classes.aside}>
                            <p className={classes.asideTitle}>Share this page</p>
                            <SocialShare url={shareUrl} title={name} />
                        </div>
                    </Grid>
                </Grid>
            </Section>

            <Section tone="muted" bordered id="directorate-publications">
                <SectionHeading
                    overline="Documents and reports"
                    title="Publications"
                    description={`Reports, budget documents, laws and guidelines published by the ${name}.`}
                />

                <FilterBar
                    resultCount={publications.loading || publications.error ? null : filtered.length}
                    resultNoun="publications"
                >
                    <FilterLabel>Search</FilterLabel>
                    <TextField
                        id="directorate-publication-search"
                        variant="outlined"
                        size="small"
                        placeholder="Search by title"
                        value={term}
                        onChange={(event) => {
                            setTerm(event.target.value);
                            setPage(1);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="disabled" />
                                </InputAdornment>
                            ),
                        }}
                        style={{ minWidth: 260 }}
                    />
                    {categories.length > 2 && (
                        <div className={classes.chips} role="group" aria-label="Filter by category">
                            {categories.map((option) => (
                                <Chip
                                    key={option}
                                    label={option}
                                    clickable
                                    color={category === option ? 'primary' : 'default'}
                                    onClick={() => {
                                        setCategory(option);
                                        setPage(1);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </FilterBar>

                {publications.loading && <LoadingState label="Loading publications" />}

                {!publications.loading && publications.error && (
                    <div role="alert">
                        <EmptyState
                            title="Unable to load publications"
                            description="This directorate's publications could not be loaded. Please refresh the page to try again."
                        />
                    </div>
                )}

                {!publications.loading && !publications.error && visible.length === 0 && (
                    <EmptyState
                        title={publications.items.length ? 'No publications match your filters' : 'No publications yet'}
                        description={
                            publications.items.length
                                ? 'Try a different search term or category.'
                                : 'Publications linked to this directorate in the CMS will appear here.'
                        }
                    />
                )}

                {!publications.loading && visible.length > 0 && (
                    <>
                        <Grid container spacing={3}>
                            {visible.map((entry) => (
                                <Grid item xs={12} sm={6} md={4} key={entry.sys.id}>
                                    <Publication publications={entry} />
                                </Grid>
                            ))}
                        </Grid>
                        <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
                    </>
                )}
            </Section>
        </>
    );
}
